<template>
  <div data-spec="main">
    <section data-test="product-results-refinement">
      <div class="mb-12 justify-between lg:mb-32 lg:flex">
        <span />
      </div>
      <div class="sticky -top-1 z-[1]">
        <TopFilters
          v-model:filters="filtersInput"
          v-model:price="priceFilter"
          v-model:sort="sortInput"
          :facets
          :price-facet
          :search-nb-results="productsSearchData.total"
          :sort-options
        />
      </div>

      <div class="mt-16 lg:flex lg:gap-16">
        <TheMainContent class="w-full">
          <span
            class="text-static-default-low body-2 mb-12 block lg:text-caption md:mb-16"
            data-selector="total-products"
            data-test="total-products"
          >
            {{
              i18n(translations.resultsCount, {
                total: productsSearchData.total,
              })
            }}
          </span>
          <SwapPill v-if="isBuybackEnabled" class="lg:hidden" />

          <NoResults
            v-if="productsSearchData.products.length === 0"
            class="my-32"
            data-spec="body"
          />
          <ResultGrid
            v-else
            class="mt-8 md:mt-32"
            :products="productsSearchData.products"
          />
        </TheMainContent>
      </div>

      <Pagination
        v-model="pageInput"
        :page-count="productsSearchData.pageCount"
      />
    </section>

    <FullscreenFilters
      v-model:filters="filtersInput"
      v-model:price="priceFilter"
      v-model:sort="sortInput"
      :facets
      :price-facet
      :search-nb-results="productsSearchData.total"
      :sort-options
    />
  </div>
</template>

<script setup lang="ts">
import { createError, useRouteParams } from '#imports'
import { ref } from 'vue'

import { useBuybackEnabled } from '@backmarket/nuxt-layer-buyback/app/composables/useBuybackEnabled/useBuybackEnabled'
import FullscreenFilters from '@backmarket/nuxt-layer-recommendation/filtering/FullscreenFilters.vue'
import TopFilters from '@backmarket/nuxt-layer-recommendation/filtering/TopFilters.vue'
import type {
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { refDebounced } from '@vueuse/core'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import SwapPill from '~/scopes/buyback/swap/components/SwapPill/SwapPill.vue'
import NoResults from '~/scopes/product-list/components/NoResults/NoResults.vue'
import Pagination from '~/scopes/product-list/components/Pagination/Pagination.vue'
import ResultGrid from '~/scopes/product-list/components/ResultGrid/ResultGrid.vue'
import { useFiltersFromHash } from '~/scopes/product-list/composables/useHashFilters'
import {
  getSortOptions,
  useAlgoliaConfiguration,
} from '~/scopes/product-list/helpers/helpers'

import { useProductsSearch } from '../../../../composables/useProductsSearch/useProductsSearch'
import { useSearchInputs } from '../../../../composables/useSearchInputs/useSearchInputs'

import translations from './ItemsTab.translations'

const i18n = useI18n()

const { uuid: sellerId } = useRouteParams()
const { isBuybackEnabled } = useBuybackEnabled()

const { data: searchConfig } = await useAlgoliaConfiguration(
  'merchant-landing',
  sellerId,
)

if (searchConfig.value === undefined) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Unable to retrieve search configuration',
    fatal: true,
  })
}

const sortOptions = getSortOptions(searchConfig.value.indexes)

const { filtersInput, sortInput, priceFilter, pageInput } = useSearchInputs(
  searchConfig.value,
)

const priceFacet = ref<UiPriceFacet | undefined>()
const facets = ref<Facet[]>([])

const debouncedPriceFilter = refDebounced(priceFilter, 500)

const { data: productsSearchData } = await useProductsSearch(
  sellerId,
  searchConfig.value,
  {
    filtersInput,
    priceInput: debouncedPriceFilter,
    sortInput,
    pageInput,
  },
)

facets.value = productsSearchData.value.facets
priceFacet.value = productsSearchData.value.priceFacet

useFiltersFromHash(filtersInput, priceFilter, pageInput)
</script>
